import { BLOCK_ICONS, type BlockIconName } from "../blocks/icons";
import {
  bindColor,
  bindRadius,
  bindTextStyle,
  brandNewDocument,
  brandTypography,
  captionTypography,
} from "../brand/apply";
import {
  type BrandLogoVariant,
  type BrandTextStyleKey,
  type BrandTokens,
  brandColor,
  brandRadius,
  brandTextStyle,
} from "../brand/tokens";
import { deviceModel } from "../devices/catalog";
import { type DeviceTilt, deviceAspect, devicePerspectives } from "../devices/layout";
import { brandChartPalette, type ChartKind, type ChartLegendPosition } from "../scene/chart";
import { contrastRatio, opaqueHex, withAlpha } from "../scene/color";
import { createEmptyDocument } from "../scene/factory";
import type { LineEnd } from "../scene/line";
import type { ImageMask } from "../scene/masks";
import {
  createChartElement,
  createDeviceElement,
  createIconElement,
  createId,
  createImageElement,
  createQrElement,
  createShapeElement,
  createSvgElement,
  createTextElement,
} from "../scene/ops";
import { type FormatPreset, getPreset } from "../scene/presets";
import {
  type BrandRadiusKey,
  type ElementAnimation,
  type Gradient,
  type Paint,
  type SceneDocument,
  type SceneElement,
  type ScenePage,
  type ShapeKind,
  sceneDocumentSchema,
  type TextEffect,
  type TextStyle,
  type TimeRange,
} from "../scene/schema";
import { curveGeometry, estimateTextLength } from "../scene/text-effects";
import {
  brandCaptionPreset,
  CAPTION_PRESETS,
  createCaptionsElement,
  getCaptionPreset,
} from "../video/presets";

/**
 * Building blocks of the template library. Templates are authored on a
 * reference page whose width is 1080 px – for videos whose shorter side is
 * 1080 px (like brand sizes, `brandScale`) – and scaled to their format.
 *
 * Colours name brand colour roles, so the same template looks right in every
 * kit:
 * - `"primary"` a brand colour, `"primary/30"` with 30 % opacity,
 * - `"on:primary"` the brand's text or background colour, whichever reads
 *   better on primary (also with opacity: `"on:primary/80"`),
 * - `"#rrggbb"` / `"#rrggbbaa"` a fixed colour.
 */
export type ColorRef = string;

export interface GradientRef {
  type: "linear" | "radial";
  /** Linear only, CSS semantics (90 = left → right). */
  angle?: number;
  /** Evenly spaced. */
  stops: ColorRef[];
}

export type FillRef = ColorRef | GradientRef;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  name?: string;
  rotation?: number;
  opacity?: number;
  /** Video: visible time range and animation. */
  time?: TimeRange;
  animation?: ElementAnimation;
}

export interface TextNode extends Omit<Box, "h"> {
  kind: "text";
  text: string;
  /** Brand text style providing font, weight, line height, spacing and case. */
  style: BrandTextStyleKey;
  /** Font size in px on the reference page. */
  size: number;
  color?: ColorRef;
  align?: TextStyle["align"];
  /**
   * Fixed box (buttons, badges): the text is centred vertically in `h` and
   * the box does not grow with the text.
   */
  h?: number;
  /**
   * Display lines: shrink the size until every line (split at `\n`) fits the
   * width. Without it only the longest word has to fit.
   */
  shrink?: boolean;
  /** Text shadow (readable text straight on a video). */
  shadow?: { x?: number; y?: number; blur?: number; color: ColorRef };
  /** Text effect (scene/text-effects.ts); `effectColor` sets its colour from the brand. */
  effect?: TextEffect;
  effectColor?: ColorRef;
  /** Curved text, −100..100 %. */
  curve?: number;
}

export interface ShapeNode extends Box {
  kind: "shape";
  shape: ShapeKind;
  fill?: FillRef;
  stroke?: { color: ColorRef; width: number; dash?: "solid" | "dashed" | "dotted" };
  radius?: BrandRadiusKey | number;
  sides?: number;
  innerRatio?: number;
  shadow?: { x?: number; y?: number; blur?: number; color: ColorRef };
  /** Lines: end caps (arrowheads etc., scene/line.ts) and round ends. */
  lineStart?: LineEnd;
  lineEnd?: LineEnd;
  lineRound?: boolean;
}

/** Spot for a photo: a shape that turns into the image dropped onto it. */
export interface ImageNode extends Box {
  kind: "image";
  fill?: ColorRef;
  radius?: BrandRadiusKey | number;
  ellipse?: boolean;
  /** Form of the spot (frames: heart, arch, blob …), kept by the image dropped onto it. */
  mask?: ImageMask;
}

/**
 * The kit's logo, fitted into the box. The variant is picked by the colour
 * behind it (`on`); without a logo the brand name is set as text.
 */
export interface LogoNode extends Box {
  kind: "logo";
  on: ColorRef;
  align?: "left" | "center" | "right";
}

export interface CaptionsNode extends Box {
  kind: "captions";
  /**
   * Look of the captions: a caption preset (`CAPTION_PRESETS`, e.g. "box" or
   * "minimal") set in the kit's heading font and accent colour. Default: the
   * brand preset.
   */
  look?: string;
}

/**
 * Texts (and spacer shapes) placed one below the other with their estimated
 * heights, anchored at the top, bottom or centre, so longer texts or wider
 * brand fonts push the others instead of overlapping them.
 */
export interface StackNode {
  kind: "stack";
  x: number;
  y: number;
  w: number;
  anchor?: "top" | "bottom" | "center";
  /** Space between items (one value or one per gap). */
  gap?: number | number[];
  align?: "left" | "center" | "right";
  items: (Omit<TextNode, "x" | "y" | "w"> | (Omit<ShapeNode, "x" | "y"> & { x?: number }))[];
}

/**
 * Device mockup (docs/19-mockups.md): a phone, tablet, laptop, screen or
 * browser window from the catalog (`devices/catalog.ts`) whose screen stays
 * empty for the user's screenshot. Give `w` or `h` and the other side follows
 * from the device's shape in its view; with both the device is fitted into
 * the box and centred in it. Brand colours do not apply (devices have their
 * own finishes); the shadow is a fixed colour or a brand role, unlinked.
 */
export interface DeviceNode extends Omit<Box, "w" | "h"> {
  kind: "device";
  /** Model id, e.g. "iphone-17-pro", "macbook-pro-14" or "browser". */
  device: string;
  /** Colour of the device (id of one of its finishes); default: the model's first. */
  finish?: string;
  /** Phones and tablets turned sideways. */
  landscape?: boolean;
  /** 3D view in degrees (`devicePerspectives` has the ready-made ones). */
  tilt?: Partial<DeviceTilt>;
  glare?: boolean;
  /** Browser windows: address in the address bar. */
  url?: string;
  w?: number;
  h?: number;
  shadow?: { x?: number; y?: number; blur?: number; color: ColorRef };
}

/**
 * An icon of the built-in library (`BLOCK_ICONS`, a small extract of the
 * Phosphor catalogue) in a brand colour.
 */
export interface IconNode {
  kind: "icon";
  icon: BlockIconName;
  x: number;
  y: number;
  w: number;
  h: number;
  color: ColorRef;
  name?: string;
  rotation?: number;
  opacity?: number;
  time?: TimeRange;
  animation?: ElementAnimation;
  shadow?: { x?: number; y?: number; blur?: number; color: ColorRef };
}

/** A chart (scene/chart.ts) in the brand's palette, text colour and body font. */
export interface ChartNode {
  kind: "chart";
  chart: ChartKind;
  x: number;
  y: number;
  w: number;
  h: number;
  labels: string[];
  series: { name: string; values: (number | null)[] }[];
  title?: string;
  unit?: string;
  decimals?: number;
  stacked?: boolean;
  showValues?: boolean;
  legend?: ChartLegendPosition;
  gridlines?: boolean;
  axis?: boolean;
  max?: number;
  hole?: number;
  /** Base text size on the reference scale. */
  fontSize: number;
  /** Text colour (default: the brand's text colour). */
  color?: ColorRef;
  name?: string;
  time?: TimeRange;
  animation?: ElementAnimation;
}

/** A QR code (value may contain {{Platzhalter}} for bulk creation). */
export interface QrNode {
  kind: "qr";
  value: string;
  x: number;
  y: number;
  /** Edge length on the reference scale. */
  size: number;
  color?: ColorRef;
  name?: string;
}

export type TemplateNode =
  | TextNode
  | ShapeNode
  | ImageNode
  | LogoNode
  | CaptionsNode
  | StackNode
  | DeviceNode
  | IconNode
  | ChartNode
  | QrNode;

export interface TemplatePage {
  name?: string;
  /** Speaker notes (presentation mode). */
  notes?: string;
  background: FillRef;
  nodes: TemplateNode[];
}

export interface TemplateLogo {
  variant: BrandLogoVariant;
  asset: { id: string; kind: string; width: number | null; height: number | null };
}

export interface BuildContext {
  tokens: BrandTokens;
  /** Keep brand links (with a kit) or store plain values (no kit). */
  linked: boolean;
  logos: readonly TemplateLogo[];
  brandName: string;
  /** Exact text measurement (browser); without it widths are estimated. */
  measure?: TextMeasure | undefined;
}

// ---------------------------------------------------------------------------
// Devices
// ---------------------------------------------------------------------------

type DeviceShape = Pick<DeviceNode, "device" | "finish" | "landscape" | "tilt" | "url">;

function deviceProps(node: DeviceShape) {
  return {
    device: node.device,
    finish: node.finish,
    landscape: node.landscape ?? false,
    tilt: { x: node.tilt?.x ?? 0, y: node.tilt?.y ?? 0, z: node.tilt?.z ?? 0 },
    url: node.url,
  };
}

/**
 * Width / height of a device node in its view, with the model's native
 * screen as content (browser windows: 16:10 until a screenshot is dropped).
 */
export function deviceNodeAspect(node: DeviceShape): number {
  const model = deviceModel(node.device);
  const wide = model.screen.width > model.screen.height;
  const turned = Boolean(node.landscape) && model.spec.kind === "slate";
  const long = Math.max(model.screen.width, model.screen.height);
  const short = Math.min(model.screen.width, model.screen.height);
  // Content aspect is height / width of the screenshot in the device's orientation.
  const contentAspect = wide !== turned ? short / long : long / short;
  return deviceAspect(deviceProps(node), contentAspect);
}

/**
 * Tilt of a ready-made view of the device (`devicePerspectives`, e.g.
 * "hero" or "lying"), so the editor shows it as chosen; unknown: straight.
 */
export function deviceView(device: string, view: string): DeviceTilt {
  const found = devicePerspectives(device).find((entry) => entry.id === view);
  return { ...(found?.tilt ?? { x: 0, y: 0, z: 0 }) };
}

/**
 * The box of a device node (reference px): one side given, the other
 * follows from the device's shape; both given, the device is fitted into
 * the box and centred.
 */
export function deviceNodeBox(
  node: DeviceShape & { x: number; y: number; w?: number | undefined; h?: number | undefined },
): { x: number; y: number; w: number; h: number } {
  const aspect = deviceNodeAspect(node);
  const { x, y, w, h } = node;
  if (w !== undefined && h !== undefined) {
    const width = Math.min(w, h * aspect);
    const height = width / aspect;
    return { x: x + (w - width) / 2, y: y + (h - height) / 2, w: width, h: height };
  }
  if (w !== undefined) return { x, y, w, h: w / aspect };
  if (h !== undefined) return { x, y, w: h * aspect, h };
  throw new Error(`Gerät ohne Größe in Vorlage: ${node.device}`);
}

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

interface ResolvedColor {
  hex: string;
  /** Brand colour key (only for solid, token-based colours). */
  key?: string | undefined;
}

export function resolveColor(tokens: BrandTokens, ref: ColorRef): ResolvedColor {
  if (ref.startsWith("#")) return { hex: ref.toLowerCase() };
  const [name = "", alphaPart] = ref.split("/");
  const alpha = alphaPart === undefined ? 1 : Number(alphaPart) / 100;
  const key = name.startsWith("on:") ? readableOn(tokens, name.slice(3)) : name;
  const color = brandColor(tokens, key);
  if (!color) throw new Error(`Unbekannte Markenfarbe in Vorlage: ${ref}`);
  return { hex: alpha < 1 ? withAlpha(color.color, alpha) : color.color, key };
}

/**
 * Text colour on a brand colour: the page colour knocked out of the colour
 * where that is readable for large text (3:1, like white on a strong brand
 * colour), otherwise whichever of text and background contrasts more.
 */
function readableOn(tokens: BrandTokens, key: string): string {
  const base = brandColor(tokens, key)?.color ?? "#ffffff";
  const ratio = (candidate: string) => {
    const color = brandColor(tokens, candidate)?.color;
    return color ? contrastRatio(color, base) : 0;
  };
  const background = ratio("background");
  if (background >= 3) return "background";
  return ratio("text") >= background ? "text" : "background";
}

/** A readable text colour on the given colour (a brand key where possible). */
function readableRef(tokens: BrandTokens, behind: ColorRef): ColorRef {
  if (!behind.startsWith("#")) return `on:${behind.replace(/^on:/, "").split("/")[0]}`;
  const base = opaqueHex(behind);
  const options = ["background", "text"].flatMap((key) => {
    const color = brandColor(tokens, key);
    return color ? [{ key, ratio: contrastRatio(color.color, base) }] : [];
  });
  options.sort((a, b) => b.ratio - a.ratio);
  return options[0] && options[0].ratio >= 3
    ? options[0].key
    : contrastRatio("#ffffff", base) > contrastRatio("#111111", base)
      ? "#ffffff"
      : "#111111";
}

function resolvePaint(
  tokens: BrandTokens,
  fill: FillRef,
): { paint: Paint; key?: string | undefined } {
  if (typeof fill === "string") {
    const color = resolveColor(tokens, fill);
    return { paint: color.hex, key: color.key };
  }
  const colors = fill.stops.map((stop) => resolveColor(tokens, stop).hex);
  const stops = colors.map((color, index) => ({
    offset: colors.length === 1 ? 0 : index / (colors.length - 1),
    color,
  }));
  const gradient: Gradient =
    fill.type === "linear"
      ? { type: "linear", angle: fill.angle ?? 180, stops }
      : { type: "radial", stops };
  return { paint: gradient };
}

// ---------------------------------------------------------------------------
// Text measurement (estimate; the editor measures exactly and adjusts heights)
// ---------------------------------------------------------------------------

/**
 * Average advance per character (spaces included) relative to the font size:
 * regular, bold, capitals regular, capitals bold. Measured in Chromium with
 * a German sample sentence.
 */
const GLYPH_WIDTH: Record<string, [number, number, number, number]> = {
  Inter: [0.51, 0.53, 0.62, 0.63],
  Montserrat: [0.54, 0.57, 0.65, 0.66],
  Poppins: [0.53, 0.55, 0.57, 0.6],
  Roboto: [0.47, 0.48, 0.57, 0.58],
  "Open Sans": [0.5, 0.54, 0.59, 0.61],
  Lato: [0.46, 0.47, 0.59, 0.59],
  "DM Sans": [0.49, 0.52, 0.57, 0.59],
  Raleway: [0.5, 0.51, 0.61, 0.61],
  Nunito: [0.48, 0.5, 0.6, 0.62],
  "Space Grotesk": [0.51, 0.51, 0.56, 0.57],
  "Playfair Display": [0.48, 0.49, 0.59, 0.61],
  Merriweather: [0.51, 0.53, 0.63, 0.64],
  Lora: [0.5, 0.52, 0.61, 0.63],
  "Source Serif 4": [0.51, 0.52, 0.62, 0.62],
  Oswald: [0.39, 0.43, 0.45, 0.49],
  "Bebas Neue": [0.35, 0.35, 0.36, 0.36],
  "Archivo Black": [0.58, 0.58, 0.7, 0.7],
  Caveat: [0.36, 0.36, 0.48, 0.49],
  Pacifico: [0.44, 0.44, 0.73, 0.73],
};

export type MeasuredStyle = Pick<
  TextStyle,
  "fontFamily" | "fontSize" | "fontWeight" | "italic" | "lineHeight" | "uppercase" | "letterSpacing"
>;

/** Width of one line of text in px (e.g. canvas `measureText` with the loaded fonts). */
export type TextMeasure = (text: string, style: MeasuredStyle) => number;

/** Relative width of a character compared to an average letter. */
function charWeight(char: string): number {
  if (char === " ") return 0.55;
  if ("iljI.,:;!|'’".includes(char)) return 0.5;
  if ("ftr".includes(char)) return 0.72;
  if ("mwMW".includes(char)) return 1.5;
  if (char !== char.toLowerCase()) return 1.12;
  return 1;
}

const SAMPLE = "Wir haben große Neuigkeiten für euch und freuen uns auf den Sommer im Hofgarten";
const sampleWeight = (text: string) =>
  [...text].reduce((sum, char) => sum + charWeight(char), 0) / text.length;
const NORMAL_WEIGHT = sampleWeight(SAMPLE);
const CAPS_WEIGHT = sampleWeight(SAMPLE.toUpperCase());

/**
 * Rough line width from average glyph widths, for when no fonts are at hand
 * (the server). Errs on the wide side.
 */
export const estimateTextWidth: TextMeasure = (text, style) => {
  const [regular, bold, capsRegular, capsBold] = GLYPH_WIDTH[style.fontFamily] ?? [
    0.53, 0.55, 0.63, 0.64,
  ];
  const heavy = style.fontWeight >= 600;
  const caps = style.uppercase;
  const factor = caps ? (heavy ? capsBold : capsRegular) : heavy ? bold : regular;
  const value = caps ? text.toUpperCase() : text;
  const weight = [...value].reduce((sum, char) => sum + charWeight(char), 0);
  const units = (weight / (caps ? CAPS_WEIGHT : NORMAL_WEIGHT)) * factor * 1.06;
  return style.fontSize * (units + style.letterSpacing * value.length);
};

/**
 * Font size at which the longest word (or, for display lines, every line)
 * fits the width, so wide brand fonts shrink instead of breaking words.
 */
export function fittingFontSize(
  text: string,
  style: MeasuredStyle,
  width: number,
  options: { wholeLines?: boolean | undefined; measure?: TextMeasure | undefined } = {},
): number {
  const measure = options.measure ?? estimateTextWidth;
  // Browsers also break after hyphens within words ("Sommer-|Sale"), not
  // after a leading minus ("-30 %"). Non-breaking spaces ("20 %") keep
  // their words together.
  const parts = options.wholeLines ? text.split("\n") : text.split(/[ \t\n]+|(?<=[\p{L}\p{N}]-)/u);
  const widest = Math.max(1, ...parts.map((part) => measure(part.trim(), style)));
  const available = width * (options.measure ? 0.98 : 0.94);
  return widest > available ? style.fontSize * (available / widest) : style.fontSize;
}

/** Height of a text box with word wrap (greedy, like the browser). */
export function estimateTextHeight(
  text: string,
  style: MeasuredStyle,
  width: number,
  measure: TextMeasure = estimateTextWidth,
): number {
  let lines = 0;
  for (const paragraph of text.split("\n")) {
    let count = 1;
    let line = "";
    for (const word of paragraph.split(/[ \t]+/).filter(Boolean)) {
      const candidate = line ? `${line} ${word}` : word;
      if (measure(candidate, style) <= width) {
        line = candidate;
        continue;
      }
      if (line) count++;
      // A word wider than the box breaks inside (overflow-wrap).
      const wordWidth = measure(word, style);
      if (wordWidth > width) {
        count += Math.ceil(wordWidth / width) - 1;
        line = "";
      } else {
        line = word;
      }
    }
    lines += count;
  }
  return Math.ceil(lines * style.fontSize * style.lineHeight);
}

// ---------------------------------------------------------------------------
// Nodes → elements
// ---------------------------------------------------------------------------

const round = (value: number) => Math.round(value * 10) / 10;

class Builder {
  readonly elements: SceneElement[] = [];

  constructor(
    readonly ctx: BuildContext,
    /** Reference px → document px. */
    readonly s: number,
    /** Size of the page in document px (caption sizes refer to its shorter side). */
    readonly frame: { width: number; height: number } = { width: 1080 * s, height: 1920 * s },
  ) {}

  private box(node: Box) {
    return {
      x: round(node.x * this.s),
      y: round(node.y * this.s),
      width: Math.max(1, round(node.w * this.s)),
      height: Math.max(1, round(node.h * this.s)),
      rotation: node.rotation ?? 0,
      opacity: node.opacity ?? 1,
      ...(node.name ? { name: node.name } : {}),
      ...(node.time ? { time: node.time } : {}),
      ...(node.animation ? { animation: node.animation } : {}),
    };
  }

  private color(element: SceneElement, slot: "fill" | "stroke" | "color", ref: ColorRef): void {
    const { hex, key } = resolveColor(this.ctx.tokens, ref);
    if (element.type === "shape" && slot === "fill") element.fill = hex;
    else if (element.type === "shape" && slot === "stroke" && element.stroke) {
      element.stroke.color = hex;
    } else if (element.type === "text" && slot === "color") element.style.color = hex;
    else if ((element.type === "icon" || element.type === "qr") && slot === "color") {
      element.color = hex;
    }
    if (this.ctx.linked && key) bindColor(element, slot, key, this.ctx.tokens);
  }

  private radius(element: SceneElement, radius: BrandRadiusKey | number | undefined): void {
    if (radius === undefined || (element.type !== "shape" && element.type !== "image")) return;
    if (typeof radius === "number") {
      element.radius = round(radius * this.s);
    } else if (this.ctx.linked) {
      bindRadius(element, radius, this.ctx.tokens, this.s);
    } else {
      element.radius = round(
        brandRadius(this.ctx.tokens, radius) * (radius === "full" ? 1 : this.s),
      );
    }
  }

  /** Typography of a text node as it will be resolved (also used for measuring). */
  textElement(node: TextNode): SceneElement {
    const style = brandTextStyle(this.ctx.tokens, node.style);
    if (!style) throw new Error(`Unbekannter Textstil in Vorlage: ${node.style}`);
    const width = Math.max(1, round(node.w * this.s));
    // Wide brand fonts: shrink instead of breaking words (or display lines).
    const designed = brandTypography(style, (this.s * node.size) / style.fontSize);
    let size =
      fittingFontSize(node.text, designed, width, {
        wholeLines: node.shrink,
        measure: this.ctx.measure,
      }) / this.s;
    if (node.h !== undefined && !node.curve) {
      // Fixed boxes (buttons, badges): shrink until the text fits the height.
      const maxHeight = node.h * this.s;
      for (let step = 0; step < 12; step++) {
        const typography = brandTypography(style, (this.s * size) / style.fontSize);
        if (estimateTextHeight(node.text, typography, width, this.ctx.measure) <= maxHeight) break;
        size *= 0.92;
      }
    }
    // The template's size relative to the kit's style, so a kit with larger
    // headings still gets the designed layout (and scales along later).
    const textScale = Math.min(
      10,
      Math.max(0.1, Math.round((size / style.fontSize) * 1000) / 1000),
    );
    const typography = brandTypography(style, this.s * textScale);
    const fixed = node.h !== undefined;
    const element = createTextElement({
      id: createId("text"),
      text: node.text,
      ...this.box({ ...node, h: node.h ?? 1 }),
      autoHeight: !fixed,
      style: {
        ...typography,
        align: node.align ?? "left",
        verticalAlign: fixed ? "middle" : "top",
      },
    });
    if (!fixed) {
      element.height = Math.max(
        1,
        estimateTextHeight(node.text, element.style, width, this.ctx.measure),
      );
    }
    if (this.ctx.linked) {
      bindTextStyle(element, style.key, this.ctx.tokens, this.s, { withColor: false });
      element.textScale = textScale;
      // bindTextStyle resolves at the style's size; the template's size wins.
      Object.assign(element.style, typography);
    }
    this.color(element, "color", node.color ?? style.color ?? "text");
    if (node.shadow) {
      element.shadow = {
        x: round((node.shadow.x ?? 0) * this.s),
        y: round((node.shadow.y ?? 4) * this.s),
        blur: round((node.shadow.blur ?? 18) * this.s),
        color: resolveColor(this.ctx.tokens, node.shadow.color).hex,
      };
    }
    if (node.effect) {
      const effect = JSON.parse(JSON.stringify(node.effect)) as TextEffect;
      if (node.effectColor && (effect.type === "highlight" || effect.type === "echo")) {
        effect.color = resolveColor(this.ctx.tokens, node.effectColor).hex;
      }
      element.effect = effect;
    }
    if (node.curve) {
      element.curve = node.curve;
      const { fontSize, letterSpacing } = element.style;
      const length = estimateTextLength(node.text, fontSize, letterSpacing);
      if (!fixed) element.height = Math.max(1, curveGeometry(length, node.curve, fontSize).height);
    }
    return element;
  }

  shapeElement(node: ShapeNode): SceneElement {
    const element = createShapeElement(node.shape, {
      id: createId("shape"),
      ...this.box(node),
      ...(node.sides ? { sides: node.sides } : {}),
      ...(node.innerRatio ? { innerRatio: node.innerRatio } : {}),
      ...(node.lineStart ? { lineStart: node.lineStart } : {}),
      ...(node.lineEnd ? { lineEnd: node.lineEnd } : {}),
      ...(node.lineRound ? { lineRound: true } : {}),
      ...(node.stroke
        ? {
            stroke: {
              color: "#000000",
              width: round(node.stroke.width * this.s),
              dash: node.stroke.dash ?? "solid",
            },
          }
        : {}),
      ...(node.shadow
        ? {
            shadow: {
              x: round((node.shadow.x ?? 0) * this.s),
              y: round((node.shadow.y ?? 12) * this.s),
              blur: round((node.shadow.blur ?? 40) * this.s),
              color: resolveColor(this.ctx.tokens, node.shadow.color).hex,
            },
          }
        : {}),
    });
    if (node.shape === "line" && element.stroke) {
      // Lines are drawn with their stroke; the box only needs room for it.
      element.height = Math.max(element.stroke.width * 3, element.height);
    }
    if (node.fill !== undefined) {
      const { paint, key } = resolvePaint(this.ctx.tokens, node.fill);
      if (typeof paint === "string" && key) this.color(element, "fill", node.fill as string);
      else if (element.type === "shape") element.fill = paint;
    } else if (element.type === "shape") {
      element.fill = "#00000000";
    }
    if (node.stroke) this.color(element, "stroke", node.stroke.color);
    this.radius(element, node.radius);
    return element;
  }

  imageElement(node: ImageNode): SceneElement {
    const element = createShapeElement(node.ellipse ? "ellipse" : "rect", {
      id: createId("placeholder"),
      ...this.box(node),
      name: node.name ?? "Bild",
      placeholder: "image",
      ...(node.mask ? { mask: { ...node.mask } } : {}),
    });
    this.color(element, "fill", node.fill ?? "surface");
    this.radius(element, node.radius);
    return element;
  }

  logoElement(node: LogoNode): SceneElement {
    const behind = resolveColor(this.ctx.tokens, node.on).hex;
    const dark =
      contrastRatio(opaqueHex(behind), "#ffffff") > contrastRatio(opaqueHex(behind), "#000000");
    const order: BrandLogoVariant[] = dark
      ? ["light", "primary", "icon", "dark"]
      : ["dark", "primary", "icon", "light"];
    const logo = order
      .map((variant) => this.ctx.logos.find((entry) => entry.variant === variant))
      .find((entry) => entry?.asset.width && entry.asset.height);
    const box = this.box(node);
    if (logo?.asset.width && logo.asset.height) {
      const ratio = Math.min(box.width / logo.asset.width, box.height / logo.asset.height);
      const width = round(logo.asset.width * ratio);
      const height = round(logo.asset.height * ratio);
      const align = node.align ?? "left";
      const x =
        align === "left"
          ? box.x
          : align === "right"
            ? box.x + box.width - width
            : box.x + (box.width - width) / 2;
      const placed = {
        ...box,
        x: round(x),
        y: round(box.y + (box.height - height) / 2),
        width,
        height,
        name: "Logo",
      };
      return logo.asset.kind === "svg"
        ? createSvgElement(logo.asset.id, { ...placed, id: createId("svg") })
        : createImageElement(logo.asset.id, { ...placed, id: createId("img"), fit: "contain" });
    }
    // No logo in the kit (or no kit): the name as a small word mark.
    const element = this.textElement({
      kind: "text",
      text: this.ctx.brandName,
      style: "heading-2",
      size: Math.min(node.h * 0.5, 44),
      color: readableRef(this.ctx.tokens, node.on),
      x: node.x,
      y: node.y,
      w: node.w,
      h: node.h,
      align: node.align ?? "left",
      ...(node.time ? { time: node.time } : {}),
      ...(node.animation ? { animation: node.animation } : {}),
    });
    element.name = "Logo";
    return element;
  }

  deviceElement(node: DeviceNode): SceneElement {
    const box = deviceNodeBox(node);
    const props = deviceProps(node);
    return createDeviceElement({
      id: createId("device"),
      ...this.box({ ...node, ...box }),
      device: props.device,
      ...(props.finish ? { finish: props.finish } : {}),
      landscape: props.landscape,
      tilt: props.tilt,
      glare: node.glare ?? false,
      ...(props.url ? { url: props.url } : {}),
      ...(node.shadow
        ? {
            shadow: {
              x: round((node.shadow.x ?? 0) * this.s),
              y: round((node.shadow.y ?? 24) * this.s),
              blur: round((node.shadow.blur ?? 60) * this.s),
              color: resolveColor(this.ctx.tokens, node.shadow.color).hex,
            },
          }
        : {}),
    });
  }

  captionsElement(node: CaptionsNode): SceneElement {
    const tokens = this.ctx.tokens;
    const look = node.look && node.look !== "brand" ? getCaptionPreset(node.look) : undefined;
    const element = createCaptionsElement(
      this.frame,
      look ?? brandCaptionPreset(tokens) ?? CAPTION_PRESETS[0],
    );
    Object.assign(element, this.box(node));
    const highlightKey = brandColor(tokens, "accent") ? "accent" : "primary";
    const highlights = element.style.highlight !== "none";
    if (this.ctx.linked) {
      bindTextStyle(element, "heading-1", tokens, 1);
      if (!look || highlights) bindColor(element, "fill", highlightKey, tokens);
    } else {
      if (look) {
        // The same look as with a kit, without links.
        const heading = brandTextStyle(tokens, "heading-1");
        if (heading) {
          Object.assign(element.style, captionTypography(heading, element.style.fontWeight));
        }
        const accent = brandColor(tokens, highlightKey);
        if (accent && highlights) element.style.highlightColor = accent.color;
      }
      delete element.brand;
    }
    return element;
  }

  stack(node: StackNode): SceneElement[] {
    const gaps = node.gap ?? 24;
    const gapAt = (index: number) => (Array.isArray(gaps) ? (gaps[index] ?? 24) : gaps);
    const placed = node.items.map((item) => {
      if (item.kind === "text") {
        const element = this.textElement({
          ...item,
          x: node.x,
          y: 0,
          w: node.w,
          align: item.align ?? node.align ?? "left",
        });
        return { element, height: element.height / this.s };
      }
      const width = item.w;
      const x =
        item.x ??
        (node.align === "center"
          ? node.x + (node.w - width) / 2
          : node.align === "right"
            ? node.x + node.w - width
            : node.x);
      const element = this.shapeElement({ ...item, x, y: 0 });
      return { element, height: item.h };
    });
    const total =
      placed.reduce((sum, entry) => sum + entry.height, 0) +
      placed.slice(1).reduce((sum, _, index) => sum + gapAt(index), 0);
    let y =
      node.anchor === "bottom"
        ? node.y - total
        : node.anchor === "center"
          ? node.y - total / 2
          : node.y;
    return placed.map((entry, index) => {
      entry.element.y = round(y * this.s);
      y += entry.height + gapAt(index);
      return entry.element;
    });
  }

  add(node: TemplateNode): void {
    switch (node.kind) {
      case "text":
        this.elements.push(this.textElement(node));
        break;
      case "shape":
        this.elements.push(this.shapeElement(node));
        break;
      case "image":
        this.elements.push(this.imageElement(node));
        break;
      case "logo":
        this.elements.push(this.logoElement(node));
        break;
      case "captions":
        this.elements.push(this.captionsElement(node));
        break;
      case "device":
        this.elements.push(this.deviceElement(node));
        break;
      case "stack":
        this.elements.push(...this.stack(node));
        break;
      case "icon":
        this.elements.push(this.iconElement(node));
        break;
      case "chart":
        this.elements.push(this.chartElement(node));
        break;
      case "qr":
        this.elements.push(this.qrElement(node));
        break;
    }
  }

  iconElement(node: IconNode): SceneElement {
    const icon = BLOCK_ICONS[node.icon];
    const element = createIconElement(node.icon, icon.weight, [...icon.paths], {
      id: createId("icon"),
      x: round(node.x * this.s),
      y: round(node.y * this.s),
      width: Math.max(1, round(node.w * this.s)),
      height: Math.max(1, round(node.h * this.s)),
      rotation: node.rotation ?? 0,
      opacity: node.opacity ?? 1,
      ...(node.name ? { name: node.name } : {}),
      ...(node.time ? { time: node.time } : {}),
      ...(node.animation ? { animation: node.animation } : {}),
      ...(node.shadow
        ? {
            shadow: {
              x: round((node.shadow.x ?? 0) * this.s),
              y: round((node.shadow.y ?? 4) * this.s),
              blur: round((node.shadow.blur ?? 18) * this.s),
              color: resolveColor(this.ctx.tokens, node.shadow.color).hex,
            },
          }
        : {}),
    });
    this.color(element, "color", node.color);
    return element;
  }

  chartElement(node: ChartNode): SceneElement {
    const tokens = this.ctx.tokens;
    const palette = brandChartPalette(tokens);
    const s = this.s;
    const element = createChartElement(node.chart, {
      id: createId("chart"),
      x: round(node.x * s),
      y: round(node.y * s),
      width: Math.max(1, round(node.w * s)),
      height: Math.max(1, round(node.h * s)),
      labels: [...node.labels],
      series: node.series.map((series) => ({ name: series.name, values: [...series.values] })),
      title: node.title ?? "",
      unit: node.unit ?? "",
      decimals: node.decimals ?? 0,
      stacked: node.stacked ?? false,
      showValues: node.showValues ?? false,
      legend: node.legend ?? "none",
      gridlines: node.gridlines ?? true,
      axis: node.axis ?? true,
      fontSize: Math.max(4, round(node.fontSize * s)),
      lineWidth: Math.max(1, round((node.fontSize / 5) * s)),
      barRadius: round((node.fontSize / 3) * s),
      fontFamily: brandTextStyle(tokens, "body")?.fontFamily ?? "Inter",
      brandPalette: this.ctx.linked && palette !== null,
      ...(palette ? { palette } : {}),
      ...(node.max !== undefined ? { max: node.max } : {}),
      ...(node.hole !== undefined ? { hole: node.hole } : {}),
      ...(node.name ? { name: node.name } : {}),
      ...(node.time ? { time: node.time } : {}),
      ...(node.animation ? { animation: node.animation } : {}),
    });
    this.color(element, "color", node.color ?? "text");
    return element;
  }

  qrElement(node: QrNode): SceneElement {
    const size = Math.max(1, round(node.size * this.s));
    const element = createQrElement(node.value, {
      id: createId("qr"),
      x: round(node.x * this.s),
      y: round(node.y * this.s),
      width: size,
      height: size,
      color: "#000000",
      ...(node.name ? { name: node.name } : {}),
    });
    if (node.color) this.color(element, "color", node.color);
    return element;
  }
}

/** Elements of template nodes at scale `s` (reference px → document px), e.g. for building blocks. */
export function buildTemplateElements(
  nodes: readonly TemplateNode[],
  ctx: BuildContext,
  s: number,
  /** Page size in document px; default: a 9:16 page (captions are sized by its shorter side). */
  frame?: { width: number; height: number },
): SceneElement[] {
  const builder = new Builder(ctx, s, frame);
  for (const node of nodes) builder.add(node);
  return builder.elements;
}

/**
 * Document px per reference px: the reference page is 1080 px wide, for
 * videos its shorter side is 1080 px (a 16:9 template is authored on
 * 1920 × 1080), matching `brandScale`, so text sizes stay put when the kit
 * is applied again.
 */
export function templateReferenceScale(
  preset: Pick<FormatPreset, "kind" | "width" | "height">,
): number {
  const reference = preset.kind === "video" ? Math.min(preset.width, preset.height) : preset.width;
  return reference / 1080;
}

/** Turns template pages into a document in the given format. */
export function buildTemplateDocument(
  presetId: string,
  pages: readonly TemplatePage[],
  ctx: BuildContext,
): SceneDocument {
  const preset = getPreset(presetId);
  if (!preset) throw new Error(`Unbekanntes Format in Vorlage: ${presetId}`);
  const s = templateReferenceScale(preset);
  let doc = createEmptyDocument(preset);
  // Print settings (print shop, paper, profile) of the kit.
  if (ctx.linked) doc = brandNewDocument(doc, ctx.tokens);
  const outPages: ScenePage[] = [];
  const elements: Record<string, SceneElement> = {};
  pages.forEach((page, index) => {
    const builder = new Builder(ctx, s, preset);
    for (const node of page.nodes) builder.add(node);
    const { paint, key } = resolvePaint(ctx.tokens, page.background);
    const background = typeof paint === "string" && paint.length === 9 ? opaqueHex(paint) : paint;
    outPages.push({
      id: `page_${index + 1}`,
      ...(page.name ? { name: page.name } : {}),
      ...(page.notes ? { notes: page.notes } : {}),
      background,
      children: builder.elements.map((element) => element.id),
      ...(ctx.linked && key && typeof paint === "string" ? { brand: { background: key } } : {}),
    });
    for (const element of builder.elements) elements[element.id] = element;
  });
  return sceneDocumentSchema.parse({ ...doc, pages: outPages, elements });
}
